/**
 * The module catalogue (spec §3.1) and the scope vocabulary (spec §3.3).
 *
 * These two tables are what a module and a scope are *called*, and which module
 * has to be on for which scope to be grantable. They decide nothing: the store
 * owns every verb, `capabilitiesFor` (in `tenant.ts`) owns the projection the
 * partner reads, and `provider-api` computes authorization as
 * `key.scopes ∩ scopes(tenant.modules)` from rows rather than from this file.
 * What lives here is the vocabulary all of that is written in.
 *
 * It is in `packages/domain` because three surfaces need it and none of them may
 * be the authority for the others:
 *
 *  * **`apps/cli`** — the operator surface where a human types `calenar`, and so
 *    the place a typo has to be caught before it becomes a row.
 *  * **`apps/admin-api`** — the same refusals, over HTTP, for the console.
 *  * **`apps/admin`** — offering the choices: a freshly created tenant has no
 *    `tenant_modules` rows at all, so the wizard's module step has nothing on the
 *    server to label, and the scope picker disables what is not grantable
 *    *before* the click (the server's refusal stays the authority).
 *
 * It lived in all three at once for three changes, each copy naming this move as
 * its successor. This is that move; there is now one table, and the drift it was
 * heading for is not available any more.
 */
/** Exactly spec §3.1's table, in its order. */
export const MODULES = [
    {
        key: 'calendar',
        kind: 'foundation',
        dependsOn: ['clients', 'services'],
        label: 'Calendar and appointments',
    },
    { key: 'clients', kind: 'foundation', dependsOn: [], label: 'Client book' },
    { key: 'services', kind: 'foundation', dependsOn: [], label: 'Service catalog' },
    { key: 'sales', kind: 'add-on', dependsOn: ['clients'], label: 'Checkout and sales' },
    { key: 'products', kind: 'add-on', dependsOn: ['sales'], label: 'Products and retail' },
    { key: 'certificates', kind: 'add-on', dependsOn: ['sales'], label: 'Gift certificates' },
    { key: 'credit', kind: 'add-on', dependsOn: ['clients'], label: 'Account credit' },
    { key: 'notifications', kind: 'add-on', dependsOn: ['clients'], label: 'Notifications' },
    { key: 'reporting', kind: 'add-on', dependsOn: [], label: 'Reporting' },
    { key: 'billing', kind: 'add-on', dependsOn: ['sales'], label: 'Billing and payments' },
    {
        key: 'booking_site',
        kind: 'add-on',
        dependsOn: ['calendar', 'clients', 'notifications'],
        label: 'Guest booking site',
    },
    { key: 'ordering', kind: 'add-on', dependsOn: ['clients', 'notifications'], label: 'Ordering' },
    { key: 'partner_api', kind: 'add-on', dependsOn: ['calendar'], label: 'Partner API' },
    { key: 'admin', kind: 'platform', dependsOn: [], label: 'Operator console' },
];
export const MODULE_KEYS = MODULES.map((m) => m.key);
export function moduleSpec(key) {
    return MODULES.find((m) => m.key === key);
}
/** The human name, falling back to the key — a label is never a reason to fail. */
export function moduleLabel(key) {
    return moduleSpec(key)?.label ?? key;
}
/**
 * Which of `module`'s dependencies are missing from `enabled`.
 *
 * Evaluated against the state the caller would *leave behind*, not the state it
 * found: enabling `products` and `sales` in one call has to succeed, and
 * checking against the old set would refuse it (spec §3.1 — the dependency is
 * validated when a module is enabled, and half-working is what it exists to
 * prevent).
 */
export function missingDependencies(enabled, module) {
    const spec = moduleSpec(module);
    if (spec === undefined)
        return [];
    return spec.dependsOn.filter((d) => !enabled.has(d));
}
/** Which enabled modules would be left depending on `module` if it went off. */
export function dependentsOf(enabled, module) {
    return MODULES.filter((m) => enabled.has(m.key) && m.dependsOn.includes(module)).map((m) => m.key);
}
/**
 * `module:action`, and **never granted by default** (spec §3.3).
 *
 * A tenant's modules say what the business has; they must not decide what a key
 * can reach. The rows below the first six are the future facets the spec names,
 * present so that the day one is sold the answer is a scope that already has a
 * module attached rather than an ad-hoc string.
 */
export const SCOPES = [
    { scope: 'catalog:read', module: 'services', grants: 'the service menu' },
    { scope: 'calendar:read', module: 'calendar', grants: 'openings, never the guest attached' },
    { scope: 'calendar:write', module: 'calendar', grants: 'hold, book, cancel, reschedule' },
    { scope: 'clients:read', module: 'clients', grants: 'identity, visits, lifetime value' },
    {
        scope: 'clients:preferences',
        module: 'clients',
        grants: 'adds preferences, tags and notes (health-adjacent — §6.6)',
    },
    { scope: 'clients:write', module: 'clients', grants: 'editing a guest profile' },
    { scope: 'certificates:read', module: 'certificates', grants: 'certificate balances' },
    { scope: 'credit:read', module: 'credit', grants: 'account credit balances' },
    { scope: 'products:read', module: 'products', grants: 'the retail catalog' },
    { scope: 'menu:read', module: 'ordering', grants: 'the prepared-item menu' },
    { scope: 'orders:read', module: 'ordering', grants: 'order state' },
    { scope: 'orders:write', module: 'ordering', grants: 'placing an order' },
];
export const SCOPE_KEYS = SCOPES.map((s) => s.scope);
export function scopeSpec(scope) {
    return SCOPES.find((s) => s.scope === scope);
}
/**
 * The key aiconcierge holds (spec §3.3) — and, as importantly, what it does not:
 * no `clients:write`, and nothing at all from products, sales, certificates,
 * credit, billing or reporting.
 *
 * Named `…_LIST` rather than `CONCIERGE_SCOPES` on purpose: `packages/store`'s
 * seed fixtures export a constant by that name, and a file that imported both
 * would be reading two different things — the spec's statement and one test
 * tenant's key — under one identifier.
 */
export const CONCIERGE_SCOPE_LIST = [
    'catalog:read',
    'calendar:read',
    'calendar:write',
    'clients:read',
    'clients:preferences',
];
//# sourceMappingURL=modules.js.map